import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Filter, X, Edit, Trash2, Plus, RefreshCw, Calendar, User, AlertTriangle, CheckSquare, DollarSign } from 'lucide-react';
import styles from './Notifications.module.css';

// Import fetch functions
import { getTasks } from '../../Fetch/fetchTasks';
import { getListPaymentsByBuildingId, getVaadMonthlySimple } from '../../Fetch/fetchPayment';
import { getListUsers } from '../../Fetch/fetchUser';
import { fetchBuildings } from '../../Fetch/fetchBuilding';

// Helper function to parse payment details
const parsePaymentDetails = (details, type) => {
  if (type !== 'תשלום' || !details) return null;
  
  const parts = details.split(' | ');
  const mainTenant = parts[0]?.replace('דייר ראשי: ', '') || 'לא זמין';
  const debtsPart = parts[1]?.replace('חובות: ', '') || '';
  const months = debtsPart.split(', ').filter(month => month.trim());
  
  return {
    mainTenant,
    months
  };
};

// PaymentDetails Component
const PaymentDetails = ({ details, type }) => {
  const parsedDetails = parsePaymentDetails(details, type);
  
  if (!parsedDetails) {
    return <span>{details || 'אין פרטים נוספים'}</span>;
  }
  
  return (
    <div className={styles.paymentDetailsContainer}>
      <div className={styles.paymentTenant}>
        דייר ראשי: {parsedDetails.mainTenant}
      </div>
      <div className={styles.paymentLabel}>
        חודשים פתוחים:
      </div>
      <div className={styles.paymentMonths}>
        {parsedDetails.months.map((month, index) => (
          <div key={index} className={styles.paymentMonth}>
            {month}
          </div>
        ))}
      </div>
    </div>
  );
};

// NotificationCard Component for Mobile View
const NotificationCard = ({ notification, onEdit, onDelete, onTaskDoubleClick, getPriorityClass, getTypeIcon }) => (
  <div 
    className={`${styles.dataCard} ${notification.type === 'משימה' && notification.isFromDatabase ? styles.taskCard : ''}`}
    onDoubleClick={() => onTaskDoubleClick(notification)}
    title={notification.type === 'משימה' && notification.isFromDatabase ? 'לחץ פעמיים לעריכת המשימה' : ''}
  >
    <div className={styles.cardHeader}>
      <div className={styles.cardTitle}>
        {getTypeIcon(notification.type)}
        <span>{notification.title}</span>
      </div>
      <span className={`${styles.priorityBadge} ${getPriorityClass(notification.priority)}`}>
        {notification.priority}
      </span>
    </div>
    <div className={styles.cardBody}>
      <div className={styles.cardInfo}>
        <Calendar size={14} />
        <span>תאריך: {notification.date}</span>
      </div>
      <div className={styles.cardInfo}>
        <User size={14} />
        <span>סוג: {notification.type}</span>
      </div>
      {notification.details && (
        <div className={styles.cardInfo}>
          <AlertTriangle size={14} />
          <div className={styles.cardDetailsContent}>
            <PaymentDetails details={notification.details} type={notification.type} />
          </div>
        </div>
      )}
      <div className={styles.cardActions}>
        <button
          className={styles.editButton}
          onClick={() => onEdit(notification)}
          disabled={notification.isFromDatabase}
          title={notification.isFromDatabase ? 'לא ניתן לערוך התראות אוטומטיות' : 'עריכה'}
        >
          <Edit size={16} />
        </button>
        <button
          className={styles.deleteButton}
          onClick={() => onDelete(notification.id)}
          disabled={notification.isFromDatabase}
          title={notification.isFromDatabase ? 'לא ניתן למחוק התראות אוטומטיות' : 'מחיקה'}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  </div>
);

const Notifications = () => {
  const navigate = useNavigate();
  
  // State for real data
  const [allNotifications, setAllNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('all');

  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('הכל');
  
  // Modal state
  const [editingNotification, setEditingNotification] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewNotification, setIsNewNotification] = useState(false);
  const [formValues, setFormValues] = useState({
    title: '',
    date: '',
    priority: 'בינונית',
    type: 'כללי'
  });

  // Statistics
  const totalNotifications = allNotifications.length;
  const totalTasks = allNotifications.filter(n => n.type === 'משימה').length;
  const totalPayments = allNotifications.filter(n => n.type === 'תשלום').length;
  const totalGeneral = allNotifications.filter(n => n.type === 'כללי' || !n.type).length;

  // Function to calculate unpaid apartments (similar to Dashboard)
  const calculateUnpaidApartments = (users, payments, buildingCreateDate = null, vaadMonthlyMap = {}) => {
    if (!users || !Array.isArray(users) || !payments || !Array.isArray(payments)) {
      return [];
    }

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    // Get building creation date to limit the months we check
    let buildingStartDate = null;
    if (buildingCreateDate && buildingCreateDate !== 'לא זמין') {
      buildingStartDate = new Date(buildingCreateDate);
    }

    const monthsToCheck = [];
    const maxMonthsToCheck = buildingStartDate ?
      Math.ceil((currentDate - buildingStartDate) / (1000 * 60 * 60 * 24 * 30)) + 1 :
      12;

    for (let i = 0; i < Math.min(maxMonthsToCheck, 24); i++) {
      const checkDate = new Date(currentYear, currentMonth - 1 - i, 1);
      if (buildingStartDate && checkDate < buildingStartDate) {
        break;
      }
      monthsToCheck.push({
        month: checkDate.getMonth() + 1,
        year: checkDate.getFullYear(),
        monthName: checkDate.toLocaleDateString('he-IL', { month: 'short', year: 'numeric' })
      });
    }

    const paymentsByApartment = {};
    payments.forEach(payment => {
      const apartmentNumber = (payment.apartmentNumber || payment.ApartmentNumber)?.toString();
      const paymentDate = new Date(payment.date || payment.Date);
      const paymentMonth = paymentDate.getMonth() + 1;
      const paymentYear = paymentDate.getFullYear();

      if (apartmentNumber && !isNaN(paymentMonth) && !isNaN(paymentYear)) {
        if (!paymentsByApartment[apartmentNumber]) {
          paymentsByApartment[apartmentNumber] = new Set();
        }
        paymentsByApartment[apartmentNumber].add(`${paymentYear}-${paymentMonth}`);
      }
    });

    // Group users by apartment
    const usersByApartment = {};
    users.forEach(user => {
      const userApartment = user.apartmentNumber?.toString();
      if (userApartment) {
        if (!usersByApartment[userApartment]) {
          usersByApartment[userApartment] = [];
        }
        usersByApartment[userApartment].push(user);
      }
    });

    const unpaidApartments = [];

    Object.entries(usersByApartment).forEach(([apartmentNumber, apartmentUsers]) => {
      const apartmentPayments = paymentsByApartment[apartmentNumber] || new Set();
      const unpaidMonths = [];

      monthsToCheck.forEach(monthData => {
        const monthKey = `${monthData.year}-${monthData.month}`;
        if (!apartmentPayments.has(monthKey)) {
          unpaidMonths.push(monthData.monthName);
        }
      });

      if (unpaidMonths.length > 0) {
        const vaadMonthly = vaadMonthlyMap[apartmentNumber] ||
          apartmentUsers[0]?.vaadMonthly ||
          apartmentUsers[0]?.VaadMonthly ||
          500;

        const apartmentData = {
          apartmentNumber: apartmentNumber,
          users: apartmentUsers.map(user => ({
            name: user.fullName || user.FullName || 'לא זמין',
            email: user.email || user.Email || '',
            phone: user.phone || user.PhoneNumber || ''
          })),
          unpaidMonths: unpaidMonths,
          monthsUnpaidCount: unpaidMonths.length,
          vaadMonthly: vaadMonthly,
          totalDebt: unpaidMonths.length * vaadMonthly
        };

        unpaidApartments.push(apartmentData);
      }
    });

    return unpaidApartments.sort((a, b) => b.monthsUnpaidCount - a.monthsUnpaidCount);
  };

  // Function to create notifications from database data
  const createNotificationsFromData = (openTasks, unpaidApartments) => {
    const notifications = [];
    let id = 1;

    // Create notifications for open tasks
    openTasks.forEach(task => {
      notifications.push({
        id: `task-${task.tasksID || id}`,
        title: `${task.description}`,
        date: new Date(task.date).toLocaleDateString('he-IL'),
        priority: task.priority || 'בינונית',
        type: 'משימה',
        sourceData: task,
        isFromDatabase: true,
        details: `אחראי: ${task.taskUser || 'לא צוין'} | תאריך יעד: ${new Date(task.date).toLocaleDateString('he-IL')}`
      });
      id++;
    });

    // Create notifications for unpaid apartments
    unpaidApartments.forEach(apartment => {
      const priority = apartment.monthsUnpaidCount >= 3 ? 'גבוהה' : 'בינונית';
      const mainTenant = apartment.users[0];
      
      notifications.push({
        id: `payment-${apartment.apartmentNumber}`,
        title: `דירה ${apartment.apartmentNumber} - חוב ${apartment.monthsUnpaidCount} חודשים (₪${apartment.totalDebt.toLocaleString()})`,
        date: new Date().toLocaleDateString('he-IL'),
        priority: priority,
        type: 'תשלום',
        sourceData: apartment,
        isFromDatabase: true,
        details: `דייר ראשי: ${mainTenant?.name || 'לא זמין'} | חובות: ${apartment.unpaidMonths.join(', ')}`
      });
    });

    return notifications;
  };

  // Load data from database
  const loadNotificationsData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        throw new Error('משתמש לא מחובר');
      }

      const user = JSON.parse(storedUser);
      const buildingId = user.buildingId;

      if (!buildingId) {
        throw new Error('לא נמצא מזהה בניין');
      }

      // Fetch all required data including building information
      const [tasksData, paymentsData, usersData, vaadMonthlyData, buildingData] = await Promise.all([
        getTasks(buildingId),
        getListPaymentsByBuildingId(buildingId),
        getListUsers(buildingId),
        getVaadMonthlySimple(buildingId).catch(() => ({})), // Handle VaadMonthly gracefully
        fetchBuildings(1, 1000, '').then(result => {
          // Find the specific building from the results
          const building = result.items?.find(b => b.buildingID === parseInt(buildingId));
          return building;
        }).catch(() => null) // Handle building fetch gracefully
      ]);

      // Filter open tasks
      const openTasks = tasksData?.tasks?.filter(task => task.status === 'פתוח') || [];

      // Extract building creation date
      const buildingCreateDate = buildingData?.createDate || null;
      

      // Calculate unpaid apartments with building creation date
      const unpaidApartments = calculateUnpaidApartments(
        usersData || [],
        paymentsData || [],
        buildingCreateDate, // Now passing the actual building creation date
        vaadMonthlyData || {}
      );

      // Create notifications from the data
      const databaseNotifications = createNotificationsFromData(openTasks, unpaidApartments);
      
      // Add any existing manual notifications from localStorage
      const savedManualNotifications = JSON.parse(localStorage.getItem('manualNotifications') || '[]');
      
      // Combine database and manual notifications
      const allNotifications = [...databaseNotifications, ...savedManualNotifications];
      
      setAllNotifications(allNotifications);

    } catch (error) {
      setError('שגיאה בטעינת הנתונים: ' + error.message);
      
      // Fallback to manual notifications only
      const savedManualNotifications = JSON.parse(localStorage.getItem('manualNotifications') || '[]');
      setAllNotifications(savedManualNotifications);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadNotificationsData();
  }, []);

  // Filter notifications based on active tab
  useEffect(() => {
    let filteredByTab = allNotifications;
    
    // Filter by tab
    if (activeTab !== 'all') {
      filteredByTab = allNotifications.filter(notification => notification.type === activeTab);
    }
    
    // Apply search filter
    if (searchTerm) {
      filteredByTab = filteredByTab.filter(item => 
        item.title.includes(searchTerm) || 
        item.date.includes(searchTerm) ||
        (item.details && item.details.includes(searchTerm))
      );
    }
    
    // Apply priority filter
    if (priorityFilter !== 'הכל') {
      filteredByTab = filteredByTab.filter(item => item.priority === priorityFilter);
    }
    
    setFilteredNotifications(filteredByTab);
    setCurrentPage(1);
  }, [activeTab, searchTerm, priorityFilter, allNotifications]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredNotifications.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePriorityFilterChange = (e) => {
    setPriorityFilter(e.target.value);
  };

  // Priority styling
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'גבוהה': return styles.priorityHigh;
      case 'בינונית': return styles.priorityMedium;
      case 'נמוכה': return styles.priorityLow;
      default: return '';
    }
  };

  // Type icon helper
  const getTypeIcon = (type) => {
    switch (type) {
      case 'משימה': return <CheckSquare size={14} />;
      case 'תשלום': return <DollarSign size={14} />;
      default: return <User size={14} />;
    }
  };

  // Handle double-click on task notifications
  const handleTaskDoubleClick = (notification) => {
    if (notification.type === 'משימה' && notification.isFromDatabase && notification.sourceData) {
      // Navigate to TaskManagement with the task data for editing
      navigate('/app/tasks', { 
        state: { 
          editingTask: notification.sourceData,
          fromNotifications: true 
        } 
      });
    }
  };

  // Tab change handler
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
  };

  // Manual notification management
  const handleAddNotification = () => {
    setIsNewNotification(true);
    setFormValues({
      title: '',
      date: '',
      priority: 'בינונית',
      type: 'כללי'
    });
    setIsModalOpen(true);
  };
  
  const handleEditNotification = (notification) => {
    if (notification.isFromDatabase) {
      alert('לא ניתן לערוך התראות שנוצרו אוטומטית מהמערכת');
      return;
    }
    
    setIsNewNotification(false);
    setEditingNotification(notification);
    setFormValues({
      title: notification.title,
      date: notification.date,
      priority: notification.priority,
      type: notification.type || 'כללי'
    });
    setIsModalOpen(true);
  };

  const handleDeleteNotification = (id) => {
    const notification = allNotifications.find(n => n.id === id);
    
    if (notification?.isFromDatabase) {
      alert('לא ניתן למחוק התראות שנוצרו אוטומטית מהמערכת');
      return;
    }
    
    if (window.confirm('האם אתה בטוח שברצונך למחוק התראה זו?')) {
      const updatedNotifications = allNotifications.filter(item => item.id !== id);
      setAllNotifications(updatedNotifications);
      
      // Update localStorage for manual notifications
      const manualNotifications = updatedNotifications.filter(n => !n.isFromDatabase);
      localStorage.setItem('manualNotifications', JSON.stringify(manualNotifications));
    }
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };
  
  const handleSaveNotification = () => {
    if (formValues.title.trim() === '' || formValues.date.trim() === '') {
      alert('נא למלא את כל השדות הנדרשים');
      return;
    }
    
    if (isNewNotification) {
      const newNotification = {
        id: `manual-${Date.now()}`,
        title: formValues.title,
        date: formValues.date,
        priority: formValues.priority,
        type: formValues.type,
        isFromDatabase: false,
        details: 'התראה ידנית'
      };
      const updatedNotifications = [...allNotifications, newNotification];
      setAllNotifications(updatedNotifications);
      
      // Save manual notifications to localStorage
      const manualNotifications = updatedNotifications.filter(n => !n.isFromDatabase);
      localStorage.setItem('manualNotifications', JSON.stringify(manualNotifications));
    } else {
      const updatedNotifications = allNotifications.map(item => 
        item.id === editingNotification.id ? 
        { ...item, title: formValues.title, date: formValues.date, priority: formValues.priority, type: formValues.type } : 
        item
      );
      setAllNotifications(updatedNotifications);
      
      // Save manual notifications to localStorage
      const manualNotifications = updatedNotifications.filter(n => !n.isFromDatabase);
      localStorage.setItem('manualNotifications', JSON.stringify(manualNotifications));
    }
    
    setIsModalOpen(false);
  };

  useEffect(() => {
  },[currentItems])

  // Modal escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };
    
    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  if (isLoading) {
    return (
      <div className={styles.notificationsManagement}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>טוען התראות...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.notificationsManagement}>
        <div className={styles.errorContainer}>
          <AlertTriangle size={32} />
          <p>{error}</p>
          <button onClick={loadNotificationsData} className={styles.retryButton}>
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.notificationsManagement}>
      <div className={styles.header}>
        <h1>ניהול התראות</h1>
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <AlertTriangle size={24} className={styles.statIcon} />
            <div>
              <h3>סה"כ התראות</h3>
              <p className={styles.statValue}>{totalNotifications}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <CheckSquare size={24} className={styles.statIcon} />
            <div>
              <h3>משימות פתוחות</h3>
              <p className={styles.statValue}>{totalTasks}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <DollarSign size={24} className={styles.statIcon} />
            <div>
              <h3>חובות תשלום</h3>
              <p className={styles.statValue}>{totalPayments}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'all' ? styles.active : ''}`}
          onClick={() => handleTabChange('all')}
        >
          <Filter size={18} />
          כל ההתראות ({totalNotifications})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'משימה' ? styles.active : ''}`}
          onClick={() => handleTabChange('משימה')}
        >
          <CheckSquare size={18} />
          משימות פתוחות ({totalTasks})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'תשלום' ? styles.active : ''}`}
          onClick={() => handleTabChange('תשלום')}
        >
          <DollarSign size={18} />
          חובות תשלום ({totalPayments})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'כללי' ? styles.active : ''}`}
          onClick={() => handleTabChange('כללי')}
        >
          <User size={18} />
          התראות כלליות ({totalGeneral})
        </button>
      </div>

      <div className={styles.actions}>
        <div className={styles.filtersContainer}>
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="חיפוש התראות..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Search size={20} className={styles.searchIcon} />
          </div>
          
          <div className={styles.priorityFilter}>
            <select 
              value={priorityFilter}
              onChange={handlePriorityFilterChange}
              className={styles.prioritySelect}
            >
              <option value="הכל">כל העדיפויות</option>
              <option value="גבוהה">גבוהה</option>
              <option value="בינונית">בינונית</option>
              <option value="נמוכה">נמוכה</option>
            </select>
            <Filter size={18} className={styles.filterIcon} />
          </div>
        </div>
        
        <div className={styles.actionButtons}>
          <button className={styles.refreshButton} onClick={loadNotificationsData}>
            <RefreshCw size={20} />
            רענן נתונים
          </button>
          
          <button className={styles.addButton} onClick={handleAddNotification}>
            <Plus size={20} />
            הוסף התראה חדשה
          </button>
        </div>
      </div>
      
      {/* Desktop Table View */}
      <div className={styles.tableContainer}>
        <table className={styles.notificationsTable}>
          <thead>
            <tr>
              <th>סוג</th>
              <th>כותרת</th>
              <th>פרטים</th>
              <th>תאריך</th>
              <th>עדיפות</th>
           
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map(item => (
                <tr 
                  key={item.id}
                  className={`${item.type === 'משימה' && item.isFromDatabase ? styles.taskRow : ''}`}
                  onDoubleClick={() => handleTaskDoubleClick(item)}
                  title={item.type === 'משימה' && item.isFromDatabase ? 'לחץ פעמיים לעריכת המשימה' : ''}
                >
                  <td>
                    <div className={styles.typeCell}>
                      {getTypeIcon(item.type)}
                      <span>{item.type}</span>
                    </div>
                  </td>
                  <td className={styles.titleCell}>{item.title}</td>
                  <td className={styles.detailsCell}>
                    <PaymentDetails details={item.details} type={item.type} />
                  </td>
                  <td>{item.date}</td>
                  <td>
                    <span className={`${styles.priorityBadge} ${getPriorityClass(item.priority)}`}>
                      {item.priority}
                    </span>
                  </td>
                 
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className={styles.noResults}>
                  {activeTab === 'all' ? 'לא נמצאו התראות' : 
                   activeTab === 'משימה' ? 'אין משימות פתוחות' :
                   activeTab === 'תשלום' ? '🎉 כל הדיירים שילמו בזמן!' :
                   'אין התראות כלליות'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards View */}
      <div className={styles.mobileCardsContainer}>
        <div className={styles.cardsGrid}>
          {currentItems.length > 0 ? (
            currentItems.map(item => (
              <NotificationCard
                key={item.id}
                notification={item}
                onEdit={handleEditNotification}
                onDelete={handleDeleteNotification}
                onTaskDoubleClick={handleTaskDoubleClick}
                getPriorityClass={getPriorityClass}
                getTypeIcon={getTypeIcon}
              />
            ))
          ) : (
            <div className={styles.emptyState}>
              {activeTab === 'all' ? 'לא נמצאו התראות' : 
               activeTab === 'משימה' ? 'אין משימות פתוחות' :
               activeTab === 'תשלום' ? '🎉 כל הדיירים שילמו בזמן!' :
               'אין התראות כלליות'}
            </div>
          )}
        </div>
      </div>
      
      {filteredNotifications.length > 0 && (
        <div className={styles.pagination}>
          <div className={styles.paginationControls}>
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={styles.paginationButton}
            >
              <ChevronRight size={20} />
            </button>
            <span className={styles.pageInfo}>
              עמוד {currentPage} מתוך {totalPages}
            </span>
            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={styles.paginationButton}
            >
              <ChevronLeft size={20} />
            </button>
          </div>
          <div className={styles.resultsPerPage}>
            <span>התראות בעמוד:</span>
            <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </div>
          <div className={styles.totalInfo}>
            מציג {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredNotifications.length)} מתוך {filteredNotifications.length} התראות
          </div>
        </div>
      )}
      
      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div 
          className={styles.modalOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false);
            }
          }}
        >
          <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
              <h2>{isNewNotification ? 'הוספת התראה חדשה' : 'עריכת התראה'}</h2>
              <button 
                className={styles.closeButton} 
                onClick={() => setIsModalOpen(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label htmlFor="title">כותרת *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formValues.title}
                  onChange={handleFormChange}
                  placeholder="הזן כותרת להתראה"
                  autoFocus
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="date">תאריך *</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formValues.date.split('/').reverse().join('-')}
                  onChange={(e) => {
                    const dateValue = e.target.value;
                    if (dateValue) {
                      const [year, month, day] = dateValue.split('-');
                      const formattedDate = `${day}/${month}/${year}`;
                      handleFormChange({
                        target: { name: 'date', value: formattedDate }
                      });
                    } else {
                      handleFormChange(e);
                    }
                  }}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="type">סוג התראה</label>
                <select
                  id="type"
                  name="type"
                  value={formValues.type}
                  onChange={handleFormChange}
                >
                  <option value="כללי">כללי</option>
                  <option value="אחזקה">אחזקה</option>
                  <option value="אסיפה">אסיפה</option>
                  <option value="הודעה">הודעה</option>
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="priority">עדיפות</label>
                <select
                  id="priority"
                  name="priority"
                  value={formValues.priority}
                  onChange={handleFormChange}
                >
                  <option value="גבוהה">גבוהה</option>
                  <option value="בינונית">בינונית</option>
                  <option value="נמוכה">נמוכה</option>
                </select>
              </div>
            </div>
            
            <div className={styles.formActions}>
               <button 
                className={styles.saveButton} 
                onClick={handleSaveNotification}
              >
                {isNewNotification ? 'הוספה' : 'שמירה'}
              </button>
              <button 
                className={styles.cancelButton} 
                onClick={() => setIsModalOpen(false)}
              >
                ביטול
              </button>
             
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;